// EDEN · light formatting for the plan's writing boxes — the PURE half (no DOM globals, no CSS).
//
// Teachers asked for bold, italic, points, numbers and sub-points inside the plan boxes (teacher review,
// 15 Oct 2026). Storage does not change: every plan field is still ONE text column. What goes in it is a
// tiny markdown-lite, so an old plain-text plan is already valid and reads the same as before.
//
// THE STORED FORMAT (one plan field = one string; "\r\n" is read as "\n"):
//   • A line is a line. A blank line stays a blank line (never squeezed or added).
//   • "- text" is a bulleted point; "1. text" is a numbered point. Numbers are rewritten 1, 2, 3… in each
//     list when the box is saved. Old plans' "* text", "• text" and "1) text" also read as points.
//   • Two spaces of indent per level make a sub-point: "  - sub point" sits under the point above it.
//   • A line under a point, indented two spaces deeper than its marker ("  more words"), belongs to that
//     point (a line break inside the point). Blank lines between points keep the list going.
//   • **bold** and *italic*. A marker opens before a word (after a space, the line start or punctuation) and
//     closes after a word (before a space, the line end or punctuation): "**Note:**", "**cards**.", "(*see*)".
//     Stars between letters or digits never format, so "2*3 = 6 and 4*5" in an old plan stays plain text.
//     The editor writes style per word (a part-bold word is stored as a bold word), always next to a space.
//   • A literal * or \ typed by the teacher is stored as \* and \\ — typed text can never become formatting.
//   • A numbered line only joins a list when its number is the next one (or 1 starts a list), so an old
//     "2. Practice" after a paragraph stays literally "2. Practice" — no number is ever changed on screen.
//
// Nothing here rewrites stored text on its own: parse/render functions are read-only, and the editor
// (src/rich-text.ts) only writes serializeNodes() back after a real edit by the teacher.
//
// The procedure column is five boxes joined by src/plan-steps.ts serializeSteps ("1 Class Admin:" …). This
// format never emits a line of that shape: numbered points are "1. …" (with a period), continuation and
// sub-point lines are indented, and a paragraph line is exactly what the teacher typed.

import { esc } from "./dom";

// ── The model ───────────────────────────────────────────────────────────────────────────────────

/** A stretch of text with one style. */
export interface RichRun {
  text: string;
  bold: boolean;
  italic: boolean;
}

/** One line of text. `runs: []` (or only spaces) is a blank line. */
export interface RichLine {
  type: "line";
  runs: RichRun[];
}

/** A bulleted (`ordered: false`) or numbered list. */
export interface RichList {
  type: "list";
  ordered: boolean;
  items: RichItem[];
}

/** One point. `content[0]` is always its first line; later entries are more lines and sub-lists, in order. */
export interface RichItem {
  content: Array<RichLine | RichList>;
}

export type RichBlock = RichLine | RichList;

/** The minimum of a DOM node that serializeNodes() reads — real DOM nodes fit it, and tests can pass fakes.
 *  `style` is an element's inline style (CSSStyleDeclaration), read only for font-weight / font-style. */
export interface MiniNode {
  nodeType: number;
  nodeName: string;
  textContent: string | null;
  childNodes: ArrayLike<MiniNode>;
  style?: { fontWeight?: string; fontStyle?: string } | null;
}

// ── Small helpers ───────────────────────────────────────────────────────────────────────────────

const WS = /\s/;
const isWs = (ch: string): boolean => WS.test(ch);
const PUNCT = /[\p{P}\p{S}]/u;
const isPunct = (ch: string): boolean => PUNCT.test(ch);
const isBlank = (s: string): boolean => !/\S/.test(s);
const runsText = (runs: RichRun[]): string => runs.map((r) => r.text).join("");

function pushRun(runs: RichRun[], text: string, bold: boolean, italic: boolean): void {
  if (!text) return;
  const last = runs[runs.length - 1];
  if (last && last.bold === bold && last.italic === italic) last.text += text;
  else runs.push({ text, bold, italic });
}

/** Columns of leading indent: a space is 1, a tab is 2 (one level). */
function indentWidth(line: string): number {
  let w = 0;
  for (const ch of line) {
    if (ch === " ") w += 1;
    else if (ch === "\t") w += 2;
    else break;
  }
  return w;
}

// ── Text → model ────────────────────────────────────────────────────────────────────────────────

// indent · marker ("-", "*", "•", or 1–9 digits then "." / ")") · exactly one space · the rest (may be empty)
const ITEM = /^([ \t]*)(?:([-*\u2022])|(\d{1,9})[.)]) (.*)$/;

/** Parse stored text into lines and (nested) lists. Read-only: never "fixes" the text. */
export function parseRich(text: string): RichBlock[] {
  const src = String(text ?? "").replace(/\r\n?/g, "\n");
  if (src === "") return [];

  const blocks: RichBlock[] = [];
  const stack: RichList[] = []; // stack[k] = the open list at nesting level k
  let blanks = 0; // blank lines seen while a list is open — placed once the next line shows where they belong

  const lastItem = (list: RichList): RichItem => list.items[list.items.length - 1]!;
  const blankLine = (): RichLine => ({ type: "line", runs: [] });
  /** Blank lines inside a list belong to its deepest last point; otherwise they end the list. */
  const releaseBlanks = (insideList: boolean): void => {
    if (blanks === 0) return;
    if (insideList && stack.length > 0) {
      const item = lastItem(stack[stack.length - 1]!);
      for (let k = 0; k < blanks; k++) item.content.push(blankLine());
    } else {
      stack.length = 0;
      for (let k = 0; k < blanks; k++) blocks.push(blankLine());
    }
    blanks = 0;
  };

  for (const raw of src.split("\n")) {
    if (isBlank(raw)) {
      if (stack.length > 0) blanks++;
      else blocks.push(blankLine());
      continue;
    }

    const m = ITEM.exec(raw);
    if (m) {
      const ordered = m[3] !== undefined;
      const num = ordered ? Number(m[3]) : 0;
      const level = Math.min(indentWidth(m[1] ?? "") >> 1, stack.length); // never skip a level
      const cur = stack[level];
      let action: "continue" | "new" | null = null;
      if (cur) {
        if (cur.ordered === ordered && (!ordered || num === cur.items.length + 1)) action = "continue";
        else if (!ordered || num === 1) action = "new";
      } else if (!ordered || num === 1) {
        action = "new";
      }
      if (action) {
        releaseBlanks(!(action === "new" && level === 0));
        const item: RichItem = { content: [{ type: "line", runs: parseInline(m[4] ?? "") }] };
        if (action === "continue" && cur) {
          stack.length = level + 1;
          cur.items.push(item);
        } else {
          stack.length = level;
          const list: RichList = { type: "list", ordered, items: [item] };
          if (level === 0) blocks.push(list);
          else lastItem(stack[level - 1]!).content.push(list);
          stack.push(list);
        }
        continue;
      }
      // A numbered line that is not the next number: it stays a text line, number and all.
    }

    const width = indentWidth(raw);
    if (stack.length > 0 && width >= 2) {
      // An indented line under a point continues the point (a line break inside it).
      releaseBlanks(true);
      const k = Math.min(stack.length - 1, (width >> 1) - 1);
      stack.length = k + 1;
      lastItem(stack[k]!).content.push({ type: "line", runs: parseInline(raw.replace(/^[ \t]+/, "")) });
    } else {
      releaseBlanks(false);
      stack.length = 0;
      blocks.push({ type: "line", runs: parseInline(raw) });
    }
  }
  releaseBlanks(false);
  return blocks;
}

interface TextTok {
  kind: "t";
  text: string;
}
interface DelimTok {
  kind: "d";
  left: number; // asterisks not yet used as formatting
  open: boolean;
  close: boolean;
}

/** One line's inline text → styled runs. \* and \\ are literal. A run of * opens when a word follows it and a
 *  space, the line start or punctuation comes before it; it closes when a word comes before it and a space, the
 *  line end or punctuation follows ("**Note:**", "**flashcards**.", "(*see*)"). Stars between two letters or digits
 *  ("2*3", "5*(3+2)") never format. Anything unmatched stays literal text. */
function parseInline(s: string): RichRun[] {
  const toks: Array<TextTok | DelimTok> = [];
  let buf = "";
  const n = s.length;
  let i = 0;
  while (i < n) {
    const c = s[i]!;
    if (c === "\\" && i + 1 < n && (s[i + 1] === "*" || s[i + 1] === "\\")) {
      buf += s[i + 1];
      i += 2;
      continue;
    }
    if (c === "*") {
      let j = i + 1;
      while (j < n && s[j] === "*") j++;
      const prev = i > 0 ? s[i - 1]! : "";
      const next = j < n ? s[j]! : "";
      if (buf) {
        toks.push({ kind: "t", text: buf });
        buf = "";
      }
      toks.push({
        kind: "d",
        left: j - i,
        open: next !== "" && !isWs(next) && (prev === "" || isWs(prev) || isPunct(prev)),
        close: prev !== "" && !isWs(prev) && (next === "" || isWs(next) || isPunct(next)),
      });
      i = j;
      continue;
    }
    buf += c;
    i++;
  }
  if (buf) toks.push({ kind: "t", text: buf });

  // Match each closer to the nearest opener; two asterisks on both sides make bold, otherwise one makes italic;
  // leftovers stay literal. (A run between two punctuation marks may both close and then open. The editor's own
  // markers always sit next to a space or a line end, so they are never ambiguous.)
  const boldDelta = new Int32Array(toks.length + 1);
  const italDelta = new Int32Array(toks.length + 1);
  const openers: number[] = [];
  for (let t = 0; t < toks.length; t++) {
    const d = toks[t]!;
    if (d.kind !== "d") continue;
    if (d.close) {
      while (d.left > 0 && openers.length > 0) {
        const oi = openers[openers.length - 1]!;
        const o = toks[oi] as DelimTok;
        const use = o.left >= 2 && d.left >= 2 ? 2 : 1;
        o.left -= use;
        d.left -= use;
        const delta = use === 2 ? boldDelta : italDelta;
        delta[oi + 1] = (delta[oi + 1] ?? 0) + 1;
        delta[t] = (delta[t] ?? 0) - 1;
        if (o.left === 0) openers.pop();
      }
    }
    if (d.open && d.left > 0) openers.push(t);
  }

  const runs: RichRun[] = [];
  let bold = 0;
  let ital = 0;
  for (let t = 0; t < toks.length; t++) {
    bold += boldDelta[t] ?? 0;
    ital += italDelta[t] ?? 0;
    const tok = toks[t]!;
    pushRun(runs, tok.kind === "t" ? tok.text : "*".repeat(tok.left), bold > 0, ital > 0);
  }
  return runs;
}

// ── Model → text (what the editor writes back) ──────────────────────────────────────────────────

const escapeInline = (s: string): string => s.replace(/[\\*]/g, (c) => `\\${c}`);

/** Styled runs → one line of stored text. Style is applied per word: a word that is partly bold is stored as a
 *  bold word. Punctuation at a word's edges stays outside the markers when it is unstyled ("**Note**:",
 *  "(*see*)"). So every marker sits between a word and a space, line end or punctuation, and reads back exactly.
 *  Markers nest properly (closed in reverse order, reopened if still wanted). */
function inlineToText(runs: RichRun[]): string {
  let text = "";
  const b: boolean[] = [];
  const it: boolean[] = [];
  for (const r of runs) {
    text += r.text;
    for (let k = 0; k < r.text.length; k++) {
      b.push(r.bold);
      it.push(r.italic);
    }
  }
  const styled = (from: number, to: number, arr: boolean[]): boolean => {
    for (let k = from; k < to; k++) if (arr[k]) return true;
    return false;
  };

  // Words (runs of non-spaces) cut into styled pieces: [leading punctuation][core][trailing punctuation] when the
  // edge punctuation carries no style of its own, otherwise the whole word as one piece.
  const pieces: Array<{ s: number; e: number; b: boolean; i: boolean }> = [];
  const n = text.length;
  for (let i = 0; i < n; ) {
    if (isWs(text[i]!)) {
      i++;
      continue;
    }
    let j = i;
    while (j < n && !isWs(text[j]!)) j++;
    let p1 = i;
    while (p1 < j && isPunct(text[p1]!)) p1++;
    let p2 = j;
    while (p2 > p1 && isPunct(text[p2 - 1]!)) p2--;
    const edgesPlain = !styled(i, p1, b) && !styled(i, p1, it) && !styled(p2, j, b) && !styled(p2, j, it);
    if (p1 < p2 && edgesPlain) {
      if (p1 > i) pieces.push({ s: i, e: p1, b: false, i: false });
      pieces.push({ s: p1, e: p2, b: styled(p1, p2, b), i: styled(p1, p2, it) });
      if (j > p2) pieces.push({ s: p2, e: j, b: false, i: false });
    } else {
      pieces.push({ s: i, e: j, b: styled(i, j, b), i: styled(i, j, it) });
    }
    i = j;
  }

  let out = "";
  const open: Array<"b" | "i"> = [];
  let pos = 0;
  for (const piece of pieces) {
    const k = open.findIndex((m) => (m === "b" ? !piece.b : !piece.i));
    if (k >= 0) {
      for (let q = open.length - 1; q >= k; q--) out += open[q] === "b" ? "**" : "*";
      open.length = k;
    }
    out += text.slice(pos, piece.s); // the spaces before this word (nothing inside a word)
    if (piece.b && !open.includes("b")) {
      out += "**";
      open.push("b");
    }
    if (piece.i && !open.includes("i")) {
      out += "*";
      open.push("i");
    }
    out += escapeInline(text.slice(piece.s, piece.e));
    pos = piece.e;
  }
  for (let q = open.length - 1; q >= 0; q--) out += open[q] === "b" ? "**" : "*";
  return out + text.slice(pos);
}

const isEmptyItem = (item: RichItem): boolean =>
  item.content.every((c) => c.type === "line" && isBlank(runsText(c.runs)));

function emitList(list: RichList, level: number, out: string[]): boolean {
  const pad = "  ".repeat(level);
  let n = 0;
  for (const item of list.items) {
    if (isEmptyItem(item)) continue; // an empty point is not stored (a bare "- " would read back as text)
    n++;
    const [first, ...rest] = item.content;
    const head = first && first.type === "line" ? inlineToText(first.runs) : "";
    out.push(`${pad}${list.ordered ? `${n}.` : "-"} ${isBlank(head) ? "" : head}`);
    if (first && first.type === "list") emitList(first, level + 1, out);
    for (const c of rest) {
      if (c.type === "list") {
        emitList(c, level + 1, out);
        continue;
      }
      const line = inlineToText(c.runs).replace(/^\s+/, "");
      out.push(line ? `${pad}  ${line}` : "");
    }
  }
  return n > 0;
}

/** The model → stored text. parseRich(stringifyRich(x)) reads back the same lines, points and styles. */
export function stringifyRich(blocks: RichBlock[]): string {
  const out: string[] = [];
  let afterList = false;
  for (const block of blocks) {
    if (block.type === "list") {
      if (emitList(block, 0, out)) afterList = true;
      continue;
    }
    let line = inlineToText(block.runs);
    if (isBlank(line)) {
      out.push("");
      continue;
    }
    // Right after a list an indented line would read back as part of the last point — keep it a paragraph.
    if (afterList) line = line.replace(/^[ \t]+/, "");
    out.push(line);
    afterList = false;
  }
  return out.join("\n");
}

// ── DOM → model → text (the editor's write-back) ────────────────────────────────────────────────

const BLOCK = new Set([
  "DIV", "P", "LI", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "PRE", "SECTION", "ARTICLE", "HEADER",
  "FOOTER", "ASIDE", "NAV", "MAIN", "FIGURE", "FIGCAPTION", "ADDRESS", "DL", "DT", "DD", "HR", "TABLE",
  "THEAD", "TBODY", "TFOOT", "TR", "TD", "TH", "CAPTION", "FORM", "FIELDSET", "DETAILS", "SUMMARY",
]);
const SKIP = new Set([
  "SCRIPT", "STYLE", "TEMPLATE", "NOSCRIPT", "IMG", "PICTURE", "VIDEO", "AUDIO", "IFRAME", "OBJECT", "EMBED",
  "CANVAS", "SVG", "MATH", "INPUT", "SELECT", "TEXTAREA", "BUTTON", "HEAD", "TITLE", "META", "LINK",
]);

const upper = (node: MiniNode): string => String(node.nodeName ?? "").toUpperCase();
const kids = (node: MiniNode): MiniNode[] => Array.from(node.childNodes ?? []);

/** Bold/italic from an element's own inline style (Chrome sometimes writes <span style="font-weight: 700">). */
function styleMarks(node: MiniNode, bold: boolean, italic: boolean): [boolean, boolean] {
  const st = node.style;
  if (!st) return [bold, italic];
  const fw = String(st.fontWeight ?? "").trim().toLowerCase();
  if (fw) {
    const num = Number(fw);
    if (fw === "bold" || fw === "bolder" || num >= 600) bold = true;
    else if (fw === "normal" || fw === "lighter" || (num > 0 && num < 600)) bold = false;
  }
  const fs = String(st.fontStyle ?? "").trim().toLowerCase();
  if (fs) {
    if (fs === "italic" || fs.startsWith("oblique")) italic = true;
    else if (fs === "normal") italic = false;
  }
  return [bold, italic];
}

/** Inline content between block boundaries: lines split by <br> (or a raw "\n" in a pre-wrap text node). */
class Flow {
  lines: RichRun[][] = [[]];
  any = false; // saw text or a line break
  brLast = false; // the last thing was a line break (a trailing <br> only holds the line open)

  text(s: string, bold: boolean, italic: boolean): void {
    const parts = s.split("\n");
    parts.forEach((part, idx) => {
      if (idx > 0) this.br();
      if (part) {
        pushRun(this.lines[this.lines.length - 1]!, part, bold, italic);
        this.any = true;
        this.brLast = false;
      }
    });
  }

  br(): void {
    this.lines.push([]);
    this.any = true;
    this.brLast = true;
  }

  take(): RichRun[][] | null {
    if (!this.any) return null;
    const lines = this.lines;
    if (this.brLast && lines.length > 1) lines.pop();
    this.lines = [[]];
    this.any = false;
    this.brLast = false;
    return lines;
  }
}

interface Sink {
  flow: Flow;
  out: Array<RichLine | RichList>;
}

function flush(sink: Sink): void {
  const lines = sink.flow.take();
  if (lines) for (const runs of lines) sink.out.push({ type: "line", runs });
}

function walk(node: MiniNode, sink: Sink, bold: boolean, italic: boolean): void {
  if (node.nodeType === 3) {
    sink.flow.text(String(node.textContent ?? "").replace(/\u00a0/g, " ").replace(/\r/g, ""), bold, italic);
    return;
  }
  if (node.nodeType !== 1 && node.nodeType !== 11) return; // comments and the like
  const name = upper(node);
  if (name === "BR") {
    sink.flow.br();
    return;
  }
  if (SKIP.has(name)) return;
  const [b, i] = styleMarks(node, bold || name === "B" || name === "STRONG", italic || name === "I" || name === "EM");
  if (name === "UL" || name === "OL") {
    flush(sink);
    sink.out.push(listFrom(node, b, i));
    return;
  }
  if (BLOCK.has(name)) {
    flush(sink);
    const before = sink.out.length;
    for (const child of kids(node)) walk(child, sink, b, i);
    flush(sink);
    if (sink.out.length === before) sink.out.push({ type: "line", runs: [] }); // an empty block is a blank line
    return;
  }
  // <span>, <font>, <u>, <a>… are transparent: their text keeps the styles around them.
  for (const child of kids(node)) walk(child, sink, b, i);
}

function itemFrom(li: MiniNode, bold: boolean, italic: boolean): RichItem {
  const item: RichItem = { content: [] };
  const sink: Sink = { flow: new Flow(), out: item.content };
  const [b, i] = styleMarks(li, bold, italic);
  for (const child of kids(li)) walk(child, sink, b, i);
  flush(sink);
  if (item.content[0]?.type !== "line") item.content.unshift({ type: "line", runs: [] });
  return item;
}

function listFrom(node: MiniNode, bold: boolean, italic: boolean): RichList {
  const list: RichList = { type: "list", ordered: upper(node) === "OL", items: [] };
  let stray: Sink | null = null; // text or blocks sitting directly in <ul>/<ol> become a point of their own
  for (const child of kids(node)) {
    const name = child.nodeType === 1 ? upper(child) : "";
    if (name === "LI" || name === "UL" || name === "OL") {
      if (stray) {
        flush(stray);
        stray = null;
      }
      if (name === "LI") {
        list.items.push(itemFrom(child, bold, italic));
      } else {
        // Chrome's nested form <ul><li>a</li><ul><li>b</li></ul></ul>: the sub-list belongs to the point above.
        let last = list.items[list.items.length - 1];
        if (!last) {
          last = { content: [] };
          list.items.push(last);
        }
        last.content.push(listFrom(child, bold, italic));
      }
      continue;
    }
    if (child.nodeType === 3 && isBlank(String(child.textContent ?? ""))) continue; // spacing between points
    if (!stray) {
      const item: RichItem = { content: [] };
      list.items.push(item);
      stray = { flow: new Flow(), out: item.content };
    }
    walk(child, stray, bold, italic);
  }
  if (stray) flush(stray);
  for (const item of list.items) {
    if (item.content[0]?.type !== "line") item.content.unshift({ type: "line", runs: [] });
  }
  return list;
}

/** The editor's DOM → the model. Handles what Chrome, Safari and Firefox produce: bare text, <div>/<p> lines,
 *  <br> (a trailing one only holds a line open), <b>/<strong>, <i>/<em>, transparent <span>, both nested-list
 *  forms, empty blocks (blank lines) and &nbsp;. */
export function nodesToRich(root: MiniNode): RichBlock[] {
  const sink: Sink = { flow: new Flow(), out: [] };
  if (root.nodeType === 3) walk(root, sink, false, false);
  else for (const child of kids(root)) walk(child, sink, false, false);
  flush(sink);
  return sink.out;
}

/** The editor's DOM → stored text (the markdown-lite above). */
export function serializeNodes(root: MiniNode): string {
  return stringifyRich(nodesToRich(root));
}

// ── Model → HTML ────────────────────────────────────────────────────────────────────────────────

interface Tags {
  b: string;
  i: string;
}
const EDITOR_TAGS: Tags = { b: "b", i: "i" };
const VIEW_TAGS: Tags = { b: "strong", i: "em" };

function runsHtml(runs: RichRun[], tags: Tags): string {
  let html = "";
  for (const r of runs) {
    let t = esc(r.text);
    if (r.italic) t = `<${tags.i}>${t}</${tags.i}>`;
    if (r.bold) t = `<${tags.b}>${t}</${tags.b}>`;
    html += t;
  }
  return html;
}

function listHtml(list: RichList, tags: Tags): string {
  const tag = list.ordered ? "ol" : "ul";
  return `<${tag}>${list.items.map((item) => `<li>${itemHtml(item, tags)}</li>`).join("")}</${tag}>`;
}

/** A point's lines joined by <br>; a run of lines that ends empty gets one more <br> so that empty line shows. */
function itemHtml(item: RichItem, tags: Tags): string {
  let html = "";
  let lines: string[] = [];
  const flushLines = (): void => {
    if (lines.length === 0) return;
    html += lines.join("<br>");
    if (lines[lines.length - 1] === "") html += "<br>";
    lines = [];
  };
  for (const c of item.content) {
    if (c.type === "list") {
      flushLines();
      html += listHtml(c, tags);
    } else {
      lines.push(isBlank(runsText(c.runs)) ? "" : runsHtml(c.runs, tags));
    }
  }
  flushLines();
  return html;
}

/** Stored text → the HTML that goes inside the contenteditable: a <div> per line (<div><br></div> when blank),
 *  <ul>/<ol>/<li>, <b>, <i>. All text is escaped. */
export function richToEditorHtml(text: string): string {
  let html = "";
  for (const block of parseRich(text)) {
    if (block.type === "list") html += listHtml(block, EDITOR_TAGS);
    else html += `<div>${isBlank(runsText(block.runs)) ? "<br>" : runsHtml(block.runs, EDITOR_TAGS)}</div>`;
  }
  return html;
}

/** Stored text → safe read-only HTML: only <p>, <br>, <ul>, <ol>, <li>, <strong>, <em>, never an attribute,
 *  every character of text escaped. Lines next to each other share a <p> (joined by <br>); a blank line starts
 *  a new <p>; extra blank lines show as empty paragraphs. Leading/trailing blank lines are dropped. Put it in a
 *  <div> (a list cannot sit inside a <p>); `.rt-view` in styles/rich-text.css gives it calm spacing. */
export function richTextHtml(text: string | null | undefined): string {
  let html = "";
  let para: string[] = [];
  let blanks = 0;
  let started = false;
  const flushPara = (): void => {
    if (para.length) html += `<p>${para.join("<br>")}</p>`;
    para = [];
  };
  for (const block of parseRich(text ?? "")) {
    if (block.type === "line" && isBlank(runsText(block.runs))) {
      flushPara();
      blanks++;
      continue;
    }
    if (started && blanks > 1) html += "<p><br></p>".repeat(blanks - 1);
    blanks = 0;
    started = true;
    if (block.type === "list") {
      flushPara();
      html += listHtml(block, VIEW_TAGS);
    } else {
      para.push(runsHtml(block.runs, VIEW_TAGS));
    }
  }
  flushPara();
  return html;
}

/** Stored text with the markers taken out — every line's words, one line each (points included, without
 *  their "- " / "1. "; escapes resolved). For previews, and for counting the teacher's own words. */
export function richTextPlain(text: string | null | undefined): string {
  const out: string[] = [];
  const visit = (blocks: Array<RichLine | RichList>): void => {
    for (const block of blocks) {
      if (block.type === "line") out.push(runsText(block.runs));
      else for (const item of block.items) visit(item.content);
    }
  };
  visit(parseRich(text ?? ""));
  return out.join("\n");
}
